import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, X, Clock } from "lucide-react";
import { format } from "date-fns";
import { TimePicker } from "antd";
import dayjs from "dayjs";
import { cn } from "@/lib/utils";
import { DateTime } from "luxon";
import logger from "@/utils/logger";

interface DateTimeSelectorProps {
  selectedDate: string;
  onDateSelect: (date: string) => void;
  label: string;
  minDate?: Date;
  maxDate?: Date;
  isArrival?: boolean;
  isLoading?: boolean;
  error?: string;
  targetZone: string;
  hideDate?: boolean;
  baseDate?: string;
}

const calendarStyles = {
  width: "100%",
  "--rdp-cell-size": "36px",
  "--rdp-accent-color": "#4f46e5",
  "--rdp-background-color": "#f3f4f6",
} as React.CSSProperties;

const DateTimeSelector: React.FC<DateTimeSelectorProps> = ({
  selectedDate,
  onDateSelect,
  label,
  isLoading = false,
  error,
  isArrival,
  minDate,
  maxDate,
  targetZone,
  hideDate,
  baseDate,
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [time, setTime] = useState<dayjs.Dayjs | null>(null);
  const [date, setDate] = useState<string | null>(null);

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return "";
    const dt = DateTime.fromISO(dateStr, { zone: targetZone });
    return format(dt.toJSDate(), "LLL dd, y");
  };

  const handleTimeChange = (time: dayjs.Dayjs | null) => {
    if (!time) return;
    setTime(time);

    if (hideDate) {
      // Use the baseDate if provided, otherwise use today
      const baseDateTime = baseDate
        ? DateTime.fromISO(baseDate, { zone: targetZone })
        : DateTime.now().setZone(targetZone);

      const selectedDateTime = baseDateTime.set({
        hour: time.hour(),
        minute: time.minute(),
      });
      // Convert to UTC before sending back
      onDateSelect(selectedDateTime.toUTC().toISO()!);
    } else if (date) {
      const selectedDateTime = DateTime.fromISO(date, { zone: targetZone }).set(
        {
          hour: time.hour(),
          minute: time.minute(),
        }
      );
      // Convert to UTC before sending back
      onDateSelect(selectedDateTime.toUTC().toISO()!);
    }
  };

  const updateDate = (newDate: Date) => {
    logger.log("NEW DATE IS", newDate);
    const targetDate = DateTime.fromJSDate(newDate).setZone(targetZone, {
      keepLocalTime: true,
    });
    setDate(targetDate.toISO()!);

    if (time) {
      logger.log("DATE IN TARGET ZONE", targetDate.toISO());
      const selectedDateTime = targetDate.set({
        hour: time.hour(),
        minute: time.minute(),
      });
      logger.log("SELECTED DATE IS", selectedDateTime.toISO()!);
      onDateSelect(selectedDateTime.toISO()!);
      logger.log("SET DATE SELECTOR", selectedDateTime.toISO()!);
      logger.log("--------------------------------");
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDateSelect("");
  };

  const isDisabled = isLoading;
  const hasError = !!error;

  return (
    <div className="space-y-2">
      <div className={cn("flex items-start", hideDate ? "w-full" : "gap-6")}>
        {!hideDate && (
          <div className="flex-1 space-y-3">
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full h-12 rounded-xl justify-start text-left font-light text-sm relative bg-white",
                    "border-neutral-200 hover:border-indigo-300 hover:bg-neutral-50/50 transition-all duration-200",
                    !selectedDate && "text-muted-foreground",
                    hasError && "border-red-500 focus:ring-red-500",
                    isDisabled && "opacity-50 cursor-not-allowed",
                    "group"
                  )}
                  disabled={isDisabled}
                  aria-label={label}
                  aria-invalid={hasError}
                  aria-errormessage={error}
                >
                  <div
                    className={cn(
                      "w-8 h-8 rounded-lg mr-2 flex items-center justify-center transition-colors",
                      "bg-neutral-100 group-hover:bg-neutral-200"
                    )}
                  >
                    <CalendarIcon className="h-4 w-4 text-neutral-600" />
                  </div>
                  {date ? (
                    <>
                      <span className="font-light">
                        {formatDisplayDate(date)}
                      </span>
                      <button
                        onClick={handleClear}
                        className="absolute right-2 hover:bg-neutral-200/70 p-1.5 rounded-lg transition-colors"
                        aria-label="Clear date"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </>
                  ) : (
                    <span className="text-sm font-light">{label}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-1 bg-white shadow-lg rounded-xl border border-neutral-200"
                align="start"
                side="bottom"
              >
                <Calendar
                  initialFocus
                  mode="single"
                  selected={date ? new Date(date) : undefined}
                  defaultMonth={
                    selectedDate
                      ? DateTime.fromISO(selectedDate, {
                          zone: targetZone,
                        }).toJSDate()
                      : undefined
                  }
                  onSelect={(date) => {
                    if (!date) return;
                    updateDate(date);
                  }}
                  disabled={(date) => {
                    const startOfDay = new Date(date.setHours(0, 0, 0, 0));
                    const minStartOfDay = minDate
                      ? new Date(minDate.setHours(0, 0, 0, 0))
                      : null;
                    const maxStartOfDay = maxDate
                      ? new Date(maxDate.setHours(0, 0, 0, 0))
                      : null;

                    if (minStartOfDay && startOfDay < minStartOfDay)
                      return true;
                    if (maxStartOfDay && startOfDay > maxStartOfDay)
                      return true;
                    return false;
                  }}
                  numberOfMonths={1}
                  style={calendarStyles}
                  classNames={{
                    months: "flex flex-row space-x-2",
                    month: "space-y-2",
                    caption:
                      "flex justify-center pt-1 pb-2 relative items-center text-sm",
                    caption_label: "text-sm font-medium",
                    nav: "space-x-1 flex items-center",
                    nav_button: cn(
                      "h-7 w-7 bg-transparent p-0 hover:bg-neutral-100 rounded-lg transition-colors",
                      "opacity-75 hover:opacity-100 active:opacity-100"
                    ),
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse space-y-0.5",
                    head_row: "flex justify-between px-0.5 py-1",
                    head_cell:
                      "text-neutral-500 w-9 font-medium text-[0.7rem] uppercase tracking-wide",
                    row: "flex w-full mt-0.5 justify-between px-0.5",
                    cell: cn(
                      "text-center text-sm p-0 relative",
                      "first:[&:has([aria-selected])]:rounded-l-md",
                      "last:[&:has([aria-selected])]:rounded-r-md"
                    ),
                    day: cn(
                      "h-9 w-9 p-0 font-normal",
                      "rounded-lg flex items-center justify-center",
                      "hover:bg-neutral-100 transition-all duration-200",
                      "aria-selected:opacity-100",
                      "text-sm"
                    ),
                    day_selected: cn(
                      "bg-indigo-600 text-white hover:bg-indigo-600 hover:text-white",
                      "focus:bg-indigo-600 focus:text-white rounded-lg",
                      "shadow-sm"
                    ),
                    day_today: "bg-neutral-50 text-neutral-900",
                    day_outside: "text-neutral-400 opacity-50",
                    day_disabled: "text-neutral-400 opacity-50",
                    day_hidden: "invisible",
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
        )}

        <div className={cn("flex-1 space-y-3", hideDate && "w-full")}>
          <div className="relative">
            <div
              className={cn(
                "relative rounded-xl border border-neutral-200 bg-white",
                "hover:border-indigo-300 transition-all duration-200 group"
              )}
            >
              <div
                className={cn(
                  "absolute left-3 top-1/2 -translate-y-1/2",
                  "w-8 h-8 rounded-lg flex items-center justify-center",
                  "bg-neutral-100 group-hover:bg-neutral-200 transition-colors"
                )}
              >
                <Clock className="h-4 w-4 text-neutral-600" />
              </div>
              <TimePicker
                onChange={(time) => {
                  handleTimeChange(time);
                }}
                value={time}
                variant="borderless"
                suffixIcon={null}
                minuteStep={5}
                hourStep={1}
                format="h:mm a"
                use12Hours={true}
                needConfirm={false}
                disabled={isDisabled}
                className={cn(
                  "font-light text-sm rounded-xl focus:ring-0 focus:outline-none",
                  "duration-200 transition ease-in-out hover:cursor-pointer",
                  "h-12 pl-14 pr-4 w-full",
                  hasError && "border-red-500",
                  isDisabled && "opacity-50 cursor-not-allowed"
                )}
              />
            </div>
          </div>
        </div>
      </div>
      {error && (
        <p className="text-xs text-red-500 mt-1" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default DateTimeSelector;
